import {
    AggregatedRawIncomingMessage,
    InternalMessage,
    RawIncomingMessage,
    MessageChunkContent,
    SourceContent,
    MessageContent,
    MessageContentTypes,
    ChatLabelContent,
    ImageCarouselContent,
    AssistantHandoverContent,
    ClusterContent,
    ErrorContentMessage,
    UserMessageIdGenerationContent,
} from "@/utils/ai-adapters/xdc/types"

class MessageParser {
    parse(internalMessage: InternalMessage, rawIncomingMessage: RawIncomingMessage): InternalMessage {
        const { content } = rawIncomingMessage

        const parsedMessage: InternalMessage = { ...internalMessage }
        parsedMessage.chatId = rawIncomingMessage.chatId
        parsedMessage.messageId = !parsedMessage.messageId ? rawIncomingMessage.messageId : parsedMessage.messageId

        switch (content.contentType) {
            case "Message":
                this.parseMessageModel(parsedMessage, content)
                break
            case "MessageChunk":
                this.parseMessageChunkModel(parsedMessage, content)
                break
            case "ChatLabel":
                this.parseChatLabel(parsedMessage, content)
                break
            case "ImageCarousel":
                this.parseImageCarouselModel(parsedMessage, content)
                break
            case "Source":
            case "SourceTroubleshooting":
            case "SourceGeneralAnswer":
                this.parseSourceModel(parsedMessage, content)
                break
            case "SpeechToText":
                this.parseSpeechToTextModel()
                break
            case "AssistantHandover":
                this.parseAssistantHandover(parsedMessage, content)
                break
            case "UserMessageIdGeneration":
                this.parseUserMessageIdGeneration(parsedMessage, content)
                break
            case "Cluster":
                this.parseClusterModel(parsedMessage, content)
                break
            default:
                console.warn(`[MessageParser] content type ${(content as MessageContentTypes).contentType} not implemented`)
        }

        return parsedMessage
    }

    parseAggregatedMessage(internalMessage: InternalMessage, rawIncomingMessage: AggregatedRawIncomingMessage | undefined): InternalMessage {
        let parsedMessage: InternalMessage = { ...internalMessage }

        if (rawIncomingMessage) {
            for (const rawContent of rawIncomingMessage.contentAggregated) {
                parsedMessage = this.parse(parsedMessage, {
                    chatId: rawIncomingMessage.chatID,
                    messageId: rawIncomingMessage.messageId,
                    content: rawContent,
                } as RawIncomingMessage)
            }

            parsedMessage.finishedMessage = true
        }

        return parsedMessage
    }

    /*******************************************************************************************
     *                                      helper functions
     ******************************************************************************************/

    // parseMessageChunkModel
    parseMessageChunkModel = (internalMessage: InternalMessage, rawContent: MessageChunkContent) => {
        // Detect streaming error payloads
        if (rawContent.isLast) {
            try {
                const parsedContentMessage: ErrorContentMessage = JSON.parse(rawContent.message)
                if (parsedContentMessage?.recovery_mechanism) {
                    internalMessage.content = ''
                    internalMessage.errorRecovery = parsedContentMessage.recovery_mechanism
                    internalMessage.errorPayload = parsedContentMessage
                    internalMessage.role = rawContent.role ?? internalMessage.role
                    return
                }
            } catch {
                // Not a JSON error payload — fall through to normal chunk handling
            }
        }
        internalMessage.content += rawContent.message
        internalMessage.role = rawContent.role
    }

    parseSpeechToTextModel = () => {
        console.warn(`[MessageParser] parseSpeechToTextModel not implemented`)
    }

    parseSourceModel = (internalMessage: InternalMessage, rawContent: SourceContent) => {
        if (!internalMessage.sources)
            internalMessage.sources = [rawContent.customAttributes]
        else internalMessage.sources.push(rawContent.customAttributes)

        internalMessage.sources?.sort((a: { rank: number }, b: { rank: number }) => a.rank - b.rank)
    }

    parseMessageModel = (internalMessage: InternalMessage, rawContent: MessageContent) => {
        internalMessage.content = rawContent.message
        internalMessage.role = rawContent.role
        internalMessage.timestamp = rawContent.timestamp
    }

    parseChatLabel = (internalMessage: InternalMessage, rawContent: ChatLabelContent) => {
        internalMessage.label = rawContent.label
    }

    parseImageCarouselModel = (internalMessage: InternalMessage, rawContent: ImageCarouselContent) => {
        internalMessage.imageCarousel = {
            images: rawContent.images,
            showImagesLabel: rawContent.displayTriggerLabel,
        }
    }

    parseAssistantHandover = (internalMessage: InternalMessage, rawContent: AssistantHandoverContent) => {
        internalMessage.assistantHandover = {
            target: rawContent.toAssistantId
        }
    }

    parseClusterModel = (internalMessage: InternalMessage, rawContent: ClusterContent) => {
        if (!internalMessage.sourceClusters)
            internalMessage.sourceClusters = {}
        
        internalMessage.sourceClusters[rawContent.type] = {
            origin: rawContent.origin,
            line: rawContent.line,
        }
    }

    parseUserMessageIdGeneration = (internalMessage: InternalMessage, rawContent: UserMessageIdGenerationContent) => {
        internalMessage.userMessageId = rawContent.userMessageId
    }
}

export default new MessageParser()
